import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Form, FormGroup, Label, Input, Button
} from "reactstrap";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip
} from "chart.js";
import { API_BASE_URL } from "../config/api";

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

const BASE_URL = API_BASE_URL;

function GameStats() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [totals, setTotals] = useState({ total_winners: 0, total_participants: 0 });
  const [series, setSeries] = useState([]);
  const [error, setError] = useState("");

  const fetchStats = async (rangeOverride) => {
    try {
      const effectiveFrom = rangeOverride?.from || fromDate;
      const effectiveTo = rangeOverride?.to || toDate;
      const params = effectiveFrom && effectiveTo ? `?from=${effectiveFrom}&to=${effectiveTo}` : "";
      const res = await axios.get(`${BASE_URL}/game/stats${params}`);
      setTotals(res.data?.totals || { total_winners: 0, total_participants: 0 });
      setSeries(res.data?.series || []);
    } catch {
      setError("Error fetching stats.");
    }
  };

  useEffect(() => {
    const now = new Date();
    const to = now.toISOString().slice(0, 10);
    const fromDateObj = new Date(now);
    fromDateObj.setDate(now.getDate() - 29);
    const from = fromDateObj.toISOString().slice(0, 10);
    setFromDate(from);
    setToDate(to);
    fetchStats({ from, to });
  }, []);

  const applyQuickRange = (days) => {
    const now = new Date();
    const to = now.toISOString().slice(0, 10);
    const fromDateObj = new Date(now);
    fromDateObj.setDate(now.getDate() - (days - 1));
    const from = fromDateObj.toISOString().slice(0, 10);
    setFromDate(from);
    setToDate(to);
    fetchStats({ from, to });
  };

  const applyThisMonth = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    setFromDate(from);
    setToDate(to);
    fetchStats({ from, to });
  };

  const applyLastMonth = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);
    const to = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
    setFromDate(from);
    setToDate(to);
    fetchStats({ from, to });
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("en-US", {
        timeZone: "Asia/Amman",
        year: "numeric",
        month: "numeric",
        day: "numeric"
      });
    } catch {
      return String(value);
    }
  };

  const labels = series.map(s => formatDate(s.dte));
  const data = {
    labels,
    datasets: [
      {
        label: "Winners",
        data: series.map(s => s.winners),
        backgroundColor: "rgba(46,125,50,0.7)"
      },
      {
        label: "Participants",
        data: series.map(s => s.participants),
        backgroundColor: "rgba(21,101,192,0.7)"
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: { position: "bottom" }
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Game Statistics</CardTitle>
            </CardHeader>
            <CardBody>
              {error && <div className="alert alert-danger">{error}</div>}
              <Form inline className="mb-3" onSubmit={(e) => { e.preventDefault(); fetchStats(); }}>
                <FormGroup className="mr-2">
                  <Label className="mr-2">From</Label>
                  <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </FormGroup>
                <FormGroup className="mr-2">
                  <Label className="mr-2">To</Label>
                  <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </FormGroup>
                <Button color="primary">Apply</Button>
              </Form>
              <div className="mb-3 d-flex flex-wrap" style={{ gap: 8 }}>
                <Button size="sm" color="secondary" onClick={() => applyQuickRange(7)}>Last 7 Days</Button>
                <Button size="sm" color="secondary" onClick={() => applyQuickRange(30)}>Last 30 Days</Button>
                <Button size="sm" color="secondary" onClick={() => applyQuickRange(90)}>Last 90 Days</Button>
                <Button size="sm" color="secondary" onClick={applyThisMonth}>This Month</Button>
                <Button size="sm" color="secondary" onClick={applyLastMonth}>Last Month</Button>
              </div>

              <Row className="mb-3">
                <Col md="6">
                  <Card className="mb-2">
                    <CardBody>
                      <div style={{ fontSize: "28px", fontWeight: 700 }}>{totals.total_winners}</div>
                      <div>Total Winners</div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="6">
                  <Card className="mb-2">
                    <CardBody>
                      <div style={{ fontSize: "28px", fontWeight: 700 }}>{totals.total_participants}</div>
                      <div>Total Participants</div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <div style={{ height: "320px" }}>
                <Bar data={data} options={chartOptions} />
              </div>

              {series.length === 0 && (
                <div className="text-muted mt-3">No data for the selected range.</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default GameStats;
